import { Geolocation, Position } from "@capacitor/geolocation";
import { useEffect, useState } from "react";
import { getLogger } from "./logger";
import { SearchBias } from "./search";
import * as positionModule from "./position";

const logger = getLogger("geolocation");

export type GeolocationData = {
    isGeolocationAvailable: boolean
    isGeolocationEnabled: boolean
    isFirstPositionEstablished: boolean

    /**
     * Trigger a location lookup. If the geolocation library has not yet been initialized,
     * this will initialize it, which might result in a permission check.
     */
    getPosition: () => Promise<Position | undefined>

    currentPosition: Position | undefined
};

function emptyData(): GeolocationData {
    return {
        isGeolocationAvailable: false,
        isGeolocationEnabled: false,
        isFirstPositionEstablished: false,
        currentPosition: undefined,
        getPosition: async () => undefined,
    };
}

const globalGeolocationData = emptyData();

export function useGeolocationData(watchPosition: boolean): GeolocationData {
    const [ geolocationData, setGeolocationData ] = useState<GeolocationData>(() => ({ ...globalGeolocationData }));
    const [ cancelFunc, setCancelFunc ] = useState<{ cancel:() => void } | undefined>();

    useEffect(() => {
        if (cancelFunc && geolocationData.isFirstPositionEstablished) {
            cancelFunc.cancel();
        }
    }, [ geolocationData, cancelFunc ]);

    useEffect(() => {
        if (watchPosition) {
            setCancelFunc(undefined);
            return watchLocation(setGeolocationData);
        } else {
            // If we don't want to watch the position, do the normal watch process and then tear it down once we're through.
            const cancel = watchLocation(newData => {
                setGeolocationData(oldData => {
                    if (!oldData.isGeolocationEnabled
                        || !oldData.isGeolocationAvailable
                        || !oldData.isFirstPositionEstablished
                        || ((oldData.currentPosition?.timestamp ?? 0) - (newData.currentPosition?.timestamp ?? 0) > 5000)
                    ) {
                        logger.log("Updating geolocation data");
                        return newData;
                    } else {
                        return oldData;
                    }
                });
            });
            setCancelFunc({ cancel });
            return cancel;
        }
    }, [ watchPosition ]);

    return geolocationData;
}

const watchers = new Set<(value: GeolocationData) => void>();
let watchClearFunction = () => { };

// Whether watchGeolocationData() has ever run. Initialization is once-per-process, NOT
// once-per-subscription: consumers mount and unmount constantly (and several unsubscribe themselves
// after their first fix), so tying setup to "the watcher set went from empty to non-empty" meant
// re-running permission checks and, worse, discarding the last known position every time the count
// briefly hit zero. A map mounting right after that reset showed no blue dot until something asked
// for a fresh fix.
let isInitialized = false;

// Whether a native position watch is currently running. Guards against stacking duplicate watches
// (each one is a live CLLocationManager on iOS) when both the permission grant and a re-subscription
// try to start one.
let isWatchActive = false;

let initializationStatus: 'uninitialized' | 'starting' | 'done' = 'uninitialized';
// Assigned by watchGeolocationData(), which closes over the position-update plumbing. Starting a
// watch is idempotent (see isWatchActive), so callers don't need to track whether one is running.
let startPositionWatch = () => { };

function releaseWatch(callback: (value: GeolocationData) => void) {
    watchers.delete(callback);
    if (watchers.size === 0) {
        // Stop consuming battery, but keep currentPosition and the permission flags: they stay true
        // and the next subscriber gets an immediate answer rather than a blank map.
        watchClearFunction();
        watchClearFunction = () => { };
        isWatchActive = false;
    }
}

function watchLocation(setGeolocationData: (value: GeolocationData) => void) {
    const hadWatchers = watchers.size > 0;
    logger.log(`watchLocation: hadWatchers=${hadWatchers}, total after add=${watchers.size + 1}`);

    watchers.add(setGeolocationData);

    if (!isInitialized) {
        isInitialized = true;
        watchGeolocationData(globalGeolocationData);
    } else if (!hadWatchers && globalGeolocationData.isGeolocationAvailable) {
        // Restart a native watch that a previous teardown stopped.
        //
        // Deliberately does NOT push the current data to the new subscriber here. useGeolocationData
        // already seeds its state from globalGeolocationData in its useState initializer, so the
        // newcomer is not missing anything — and notifying from inside the subscribing effect hands
        // every consumer a fresh object, which re-triggers the "cancel once a position is
        // established" effect and churns subscribe -> notify -> cancel -> subscribe without settling.
        startPositionWatch();
    }

    return () => releaseWatch(setGeolocationData);
}

function watchGeolocationData(geolocationData: GeolocationData) {
    logger.log('watchGeolocationData: starting');

    const setCurrentPosition = (pos: Position | undefined) => {
        logger.log(`setCurrentPosition: lat=${pos?.coords?.latitude} lng=${pos?.coords?.longitude}`);
        globalGeolocationData.currentPosition = pos;
        if (!globalGeolocationData.isFirstPositionEstablished) {
            // TODO consider checking for accuracy, since the first is sometimes quite coarse
            globalGeolocationData.isFirstPositionEstablished = true;
        }
        watchers.forEach(w => w({ ...globalGeolocationData }));
    };

    startPositionWatch = () => {
        if (isWatchActive) {
            return;
        }
        isWatchActive = true;
        logger.log('startPositionWatch: starting watchPosition');
        const watchPromise = Geolocation.watchPosition(
            {
                // We use low accuracy to keep iPhones cool -- with high accuracy enabled,
                // the geolocation plugin requests navigation-level accuracy, and the phone
                // becomes a heater. TODO we might consider using our own geolocation plugin
                // and requesting some medium-level accuracy.
                enableHighAccuracy: false,
            },
            result => setCurrentPosition(result ?? undefined)
        );
        watchClearFunction = () => {
            watchPromise.then(watchId => {
                Geolocation.clearWatch({ id: watchId });
            });
        };
    };

    const setGeolocationAvailable = (available: boolean) => {
        logger.log(`setGeolocationAvailable: ${available}`);
        geolocationData.isGeolocationAvailable = available;
        geolocationData.isGeolocationEnabled = true;
        watchers.forEach(w => w({ ...globalGeolocationData }));
        if (available) {
            startPositionWatch();
        }
    };

    // On mount: silently resume location tracking if permission was already granted.
    // This lets search and other passive consumers benefit from a prior grant without
    // any permission prompt. If not yet granted, we stay 'uninitialized' and wait for
    // an explicit user gesture (e.g. the map's locate button) to call getPosition().
    Geolocation.checkPermissions()
        .then(result => {
            if (initializationStatus !== 'uninitialized') return;
            if (result.location === 'granted') {
                initializationStatus = 'done';
                setGeolocationAvailable(true);
            }
        })
        .catch(() => {
            // checkPermissions not supported in this environment — stay lazy.
        });

    // Called the first time getPosition() is invoked. Checks/requests permission
    // and, if granted, starts the position watcher. Geolocation is entirely opt-in:
    // nothing here runs on mount — only when the user explicitly triggers location
    // (e.g. by pressing the locate button on the map).
    const startLocating = () => {
        if (initializationStatus !== 'uninitialized') return;
        initializationStatus = 'starting';

        Geolocation.checkPermissions()
            .then(async result => {
                logger.log(`startLocating: checkPermissions → location=${result.location} coarseLocation=${result.coarseLocation}`);
                switch (result.location) {
                    case 'granted':
                        geolocationData.isGeolocationEnabled = true;
                        geolocationData.isGeolocationAvailable = true;
                        initializationStatus = 'done';
                        setGeolocationAvailable(true);
                        break;
                    case 'denied':
                        logger.log('startLocating: location permission denied');
                        initializationStatus = 'done';
                        setGeolocationAvailable(false);
                        break;
                    default: { // 'prompt' | 'prompt-with-rationale'
                        logger.log('startLocating: requesting permissions');
                        const requestResult = await Geolocation.requestPermissions({ permissions: ['location'] });
                        logger.log(`startLocating: requestPermissions → location=${requestResult.location}`);
                        if (requestResult.location === 'granted') {
                            initializationStatus = 'done';
                            setGeolocationAvailable(true);
                        } else if (requestResult.location === 'denied') {
                            initializationStatus = 'done';
                            setGeolocationAvailable(false);
                        } else {
                            initializationStatus = 'uninitialized';
                        }
                        break;
                    }
                }
            })
            .catch(err => {
                // checkPermissions is not available in all browser/Capacitor combinations.
                // Proceed optimistically — watchPosition will trigger the browser's own
                // permission prompt, and failure there will silently leave position undefined.
                logger.warn(`startLocating: checkPermissions not available: ${err}`);
                initializationStatus = 'done';
                setGeolocationAvailable(true);
            });
    };

    // Poll until the first fix lands, but always terminate. Previously this recursed forever at 100ms
    // intervals with no exit: if the user denied permission (or the device just never produced a fix)
    // it span for the lifetime of the page, and every caller's promise hung unresolved.
    const POSITION_WAIT_TIMEOUT_MS = 20000;
    const waitForInitialization = async (deadline = Date.now() + POSITION_WAIT_TIMEOUT_MS): Promise<Position | undefined> => {
        return new Promise<Position | undefined>(resolve => {
            setTimeout(() => {
                if (geolocationData.isFirstPositionEstablished) {
                    resolve(geolocationData.currentPosition);
                } else if (initializationStatus === 'done' && !geolocationData.isGeolocationAvailable) {
                    logger.log('waitForInitialization: geolocation unavailable; giving up');
                    resolve(undefined); // permission denied — no fix is ever coming
                } else if (Date.now() >= deadline) {
                    logger.warn('waitForInitialization: timed out waiting for a first position');
                    resolve(undefined);
                } else {
                    resolve(waitForInitialization(deadline));
                }
            }, 100);
        });
    };

    geolocationData.getPosition = async (): Promise<Position | undefined> => {
        if (initializationStatus === 'uninitialized') {
            startLocating();
            return waitForInitialization();
        } else if (initializationStatus === 'done') {
            const position = await Geolocation.getCurrentPosition();
            setCurrentPosition(position);
            return position;
        } else {
            return waitForInitialization();
        }
    };
}

/**
 * Captures a snapshot of the current geolocation data, or waits for a position
 * if geolocation is not yet available. This does not update as position changes,
 * so is suitable for use in UI components that should remain stable after render.
 *
 * This is its own `use` function instead of being rolled into the context so that
 * its lifecycle tracks the lifecycle of the component using it. That way, if new
 * components are created, they will have the latest position data as of their time
 * of creation, rather than the first data recorded in the context.
 */
export function usePositionSnapshot(): Position | undefined {
    const geo = useGeolocationData(false);

    const [ snapshot, setSnapshot ] = useState<Position | undefined>();

    useEffect(() => {
        if (geo.isFirstPositionEstablished) {
            setSnapshot(geo.currentPosition);
        } else {
            setSnapshot(undefined);
        }
        // This intentionally does not depend on geo.currentPosition to prevent updates
    }, [ geo.isFirstPositionEstablished ]);

    return snapshot;
}

export function currentLocation(): Position | undefined {
    return globalGeolocationData?.currentPosition;
}

export function computeBias(positionOptions: (positionModule.Position | undefined)[]): SearchBias | undefined {
    const opts = positionOptions.filter(pos => pos);
    if (opts.length > 0) {
        return { center: opts[0]!, radius: 10000 };
    } else {
        const coords = currentLocation()?.coords;
        const pos = positionModule.positionFromLatitudeLongitude(coords);
        if (pos) {
            return { center: pos, radius: 10000 };
        } else {
            return 'IP_BIAS';
        }
    }
}
